import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import AuthShell, { AUTH_LABEL_CLASS, AUTH_PRIMARY_BUTTON_CLASS, AUTH_TITLE_CLASS } from "@/components/AuthShell";
import { halamanAwal } from "@/lib/roles";

// Tombol kecil "copy" generik -- dipakai utk kunci manual & tiap kode pemulihan.
// Tidak pernah mencatat nilainya ke console, cukup salin ke clipboard.
function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Gagal menyalin. Salin manual saja.");
        }
    };
    return (
        <button
            type="button"
            onClick={copy}
            title={`Salin ${label}`}
            className="text-slate-400 hover:text-[#00877D] shrink-0"
        >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
}

// Kerangka seragam dgn halaman login (AuthShell) + judul langkah verifikasi.
function Shell({ children }) {
    return (
        <AuthShell>
            <div className="mb-6 flex flex-col items-center gap-2 text-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-slate-600">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Verifikasi 2 Langkah</div>
                <h1 className={`text-2xl ${AUTH_TITLE_CLASS}`}>Autentikator</h1>
            </div>
            {children}
        </AuthShell>
    );
}

// Alur login (sudah terdaftar): kolom kode 6 digit, atau "pakai kode pemulihan".
function ChallengeStep({ challengeToken, email, onDone }) {
    const { verifyTotp } = useAuth();
    const [useRecovery, setUseRecovery] = useState(false);
    const [code, setCode] = useState("");
    const [recoveryCode, setRecoveryCode] = useState("");
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, [useRecovery]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await verifyTotp(challengeToken, useRecovery ? { recoveryCode } : { code });
        setLoading(false);
        if (!res.ok) return toast.error(res.error);
        onDone(res.user);
    };

    return (
        <>
            <p className="text-sm text-slate-600 mb-5">
                Masukkan kode dari aplikasi authenticator di ponsel Anda{email ? <> untuk <span className="font-semibold text-slate-800">{email}</span></> : ""}.
            </p>
            <form onSubmit={submit} className="space-y-4">
                {useRecovery ? (
                    <div>
                        <Label htmlFor="recovery-code" className={AUTH_LABEL_CLASS}>Kode Pemulihan</Label>
                        <Input
                            id="recovery-code"
                            data-testid="totp-recovery-code-input"
                            ref={inputRef}
                            required
                            autoComplete="off"
                            value={recoveryCode}
                            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                            placeholder="ABCDE-FGH23"
                            className="mt-2 h-[52px] rounded-xl text-center text-lg tracking-widest font-semibold"
                        />
                    </div>
                ) : (
                    <div>
                        <Label htmlFor="totp-code" className={AUTH_LABEL_CLASS}>Kode Autentikator</Label>
                        <Input
                            id="totp-code"
                            data-testid="totp-code-input"
                            ref={inputRef}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="123456"
                            className="mt-2 h-[52px] rounded-xl text-center text-2xl tracking-[0.5em] font-semibold"
                        />
                    </div>
                )}

                <Button
                    type="submit"
                    data-testid="totp-submit-button"
                    disabled={loading || (useRecovery ? recoveryCode.length < 6 : code.length !== 6)}
                    className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                    {loading ? "Memverifikasi..." : "Verifikasi & Masuk"}
                </Button>
            </form>

            <div className="flex items-center justify-center mt-4 text-sm">
                <button
                    type="button"
                    data-testid="totp-toggle-recovery-button"
                    onClick={() => { setUseRecovery((v) => !v); setCode(""); setRecoveryCode(""); }}
                    className="min-h-10 px-2 text-[#00877D] dark:text-[#2DD4BF] font-semibold hover:underline"
                >
                    {useRecovery ? "Pakai kode autentikator" : "Gunakan kode pemulihan"}
                </button>
            </div>
        </>
    );
}

// Alur pendaftaran (WAJIB sebelum login pertama kali): QR -> konfirmasi kode -> kode pemulihan.
function EnrollStep({ enrollToken, email, onDone }) {
    const { totpEnrollStart, totpEnrollConfirm } = useAuth();
    const [starting, setStarting] = useState(true);
    const [enrollData, setEnrollData] = useState(null);
    const [code, setCode] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [pendingUser, setPendingUser] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        (async () => {
            const res = await totpEnrollStart(enrollToken);
            setStarting(false);
            if (!res.ok) return toast.error(res.error);
            setEnrollData(res);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { if (enrollData) inputRef.current?.focus(); }, [enrollData]);

    const submitConfirm = async (e) => {
        e.preventDefault();
        setConfirming(true);
        const res = await totpEnrollConfirm(enrollToken, code);
        setConfirming(false);
        if (!res.ok) return toast.error(res.error);
        setRecoveryCodes(res.recoveryCodes || []);
        setPendingUser(res.user);
    };

    if (starting) {
        return <p className="text-sm text-slate-500">Menyiapkan pendaftaran authenticator...</p>;
    }
    if (!enrollData) {
        return <p className="text-sm text-rose-600">Gagal memuat pendaftaran. Silakan login ulang.</p>;
    }

    if (recoveryCodes) {
        return (
            <>
                <p className="text-sm text-slate-600 mb-3">
                    Authenticator berhasil diaktifkan. <b>Simpan 10 kode pemulihan</b> di bawah ini di tempat aman
                    (mis. password manager) -- tiap kode hanya bisa dipakai <b>satu kali</b> kalau ponsel Anda hilang
                    atau tidak bisa mengakses aplikasi authenticator. Kode ini <b>tidak akan ditampilkan lagi</b>
                    setelah halaman ini ditutup.
                </p>
                <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4" data-testid="totp-recovery-codes">
                    {recoveryCodes.map((rc) => (
                        <div key={rc} className="flex items-center justify-between gap-1.5 font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                            <span>{rc}</span>
                            <CopyButton value={rc} label="kode pemulihan" />
                        </div>
                    ))}
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700 mb-4 cursor-pointer">
                    <Checkbox
                        data-testid="totp-recovery-saved-checkbox"
                        checked={savedConfirmed}
                        onCheckedChange={(v) => setSavedConfirmed(v === true)}
                        className="mt-0.5"
                    />
                    Saya sudah menyimpan kode pemulihan ini di tempat yang aman.
                </label>
                <Button
                    type="button"
                    data-testid="totp-enroll-done-button"
                    disabled={!savedConfirmed}
                    onClick={() => onDone(pendingUser)}
                    className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                    Lanjutkan
                </Button>
            </>
        );
    }

    return (
        <>
            <p className="text-sm text-slate-600 mb-4">
                Akun Super Admin{email ? <> <span className="font-semibold text-slate-800">{email}</span></> : ""} wajib
                memakai aplikasi authenticator (Google Authenticator, Microsoft Authenticator, Authy, 1Password, dst).
                Pindai QR berikut lewat aplikasi tersebut:
            </p>
            <div className="flex justify-center mb-4 bg-white dark:!bg-white p-3 rounded-lg border border-slate-200 w-fit mx-auto">
                <QRCodeSVG value={enrollData.otpauth_uri} size={176} />
            </div>
            <div className="mb-4">
                <Label className={AUTH_LABEL_CLASS}>Atau masukkan kunci manual</Label>
                <div className="mt-1.5 flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-sm tracking-wider">
                    <span className="break-all" data-testid="totp-manual-secret">{enrollData.secret}</span>
                    <CopyButton value={enrollData.secret} label="kunci manual" />
                </div>
            </div>
            <form onSubmit={submitConfirm} className="space-y-4">
                <div>
                    <Label htmlFor="enroll-code" className={AUTH_LABEL_CLASS}>
                        Kode dari Aplikasi Authenticator
                    </Label>
                    <Input
                        id="enroll-code"
                        data-testid="totp-enroll-code-input"
                        ref={inputRef}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="123456"
                        className="mt-2 h-[52px] rounded-xl text-center text-2xl tracking-[0.5em] font-semibold"
                    />
                </div>
                <Button
                    type="submit"
                    data-testid="totp-enroll-confirm-button"
                    disabled={confirming || code.length !== 6}
                    className={AUTH_PRIMARY_BUTTON_CLASS}
                >
                    {confirming ? "Memverifikasi..." : "Konfirmasi & Aktifkan"}
                </Button>
            </form>
        </>
    );
}

export default function VerifyTotp() {
    const location = useLocation();
    const navigate = useNavigate();
    const challengeToken = location.state?.challengeToken || null;
    const enrollToken = location.state?.enrollToken || null;
    const email = location.state?.email || "";

    if (!challengeToken && !enrollToken) return <Navigate to="/login" replace />;

    const handleDone = (user) => {
        toast.success("Login berhasil");
        if (user?.must_change_password) navigate("/ubah-password");
        else navigate(halamanAwal(user?.role));
    };

    return (
        <Shell>
            {challengeToken ? (
                <ChallengeStep challengeToken={challengeToken} email={email} onDone={handleDone} />
            ) : (
                <EnrollStep enrollToken={enrollToken} email={email} onDone={handleDone} />
            )}
            {challengeToken && (
                <div className="text-center mt-4">
                    <button type="button" onClick={() => navigate("/login")} className="min-h-10 px-2 text-sm text-slate-600 hover:underline">
                        Kembali ke login
                    </button>
                </div>
            )}
        </Shell>
    );
}
